import { supabase } from '../lib/supabase';
import { createAuthenticatedStream } from '../utils/transport/sseClient';
import type { Message } from '../types/chat.types';
import { v4 as uuidv4 } from 'uuid';

/**
 * Industrial-Grade Chat Service for ORION
 * 
 * Responsibilities:
 * - Data fetching and persistence (Supabase)
 * - Streaming transport management
 * - Request orchestration
 */
export class ChatService {
    /**
     * Fetches all messages for a specific conversation.
     * Includes abort signal support for cancellation.
     */
    static async getMessages(conversationId: string, signal?: AbortSignal): Promise<Message[]> {
        const query = supabase
            .from('messages')
            .select('*')
            .eq('conversation_id', conversationId)
            .order('created_at', { ascending: true });

        if (signal) {
            query.abortSignal(signal);
        }

        const { data, error } = await query;
        if (error) throw error;
        return (data || []) as unknown as Message[];
    }

    /**
     * Persists a human message to the database.
     */
    static async saveUserMessage(conversationId: string, content: string): Promise<Message> {
        const { data, error } = await supabase
            .from('messages')
            .insert({
                conversation_id: conversationId,
                role: 'user',
                content
            })
            .select()
            .single();

        if (error) throw error;
        return data as unknown as Message;
    }

    /**
     * Persists an assistant message to the database.
     * Note: Backend now also handles background persistence, but we maintain
     * this for completeness and immediate frontend sync if needed.
     */
    static async saveAssistantMessage(conversationId: string, content: string, messageId?: string): Promise<Message> {
        const payload: any = {
            conversation_id: conversationId,
            role: 'assistant',
            content
        };
        if (messageId) payload.id = messageId;

        const { data, error } = await supabase
            .from('messages')
            .insert(payload)
            .select()
            .single();

        if (error) throw error;
        return data as unknown as Message;
    }

    /**
     * Updates message feedback.
     */
    static async updateFeedback(messageId: string, feedback: 'liked' | 'disliked' | null): Promise<void> {
        const { error } = await supabase
            .from('messages')
            .update({ feedback })
            .eq('id', messageId);

        if (error) throw error;
    }

    /**
     * Orchestrates the chat completion stream with retry support.
     * 
     * CRITICAL: Generates stable message_id (UUID) BEFORE streaming.
     * This ensures retries use the same ID for idempotency.
     */
    static async streamChat(params: {
        agentId: string;
        conversationId: string;
        messages: Array<{ role: string; content: string }>;
        messageId?: string;
        retryAttempt?: number;
        onOpen?: () => void;
        onMessage: (delta: string, id: string) => void;
        onError: (err: any) => void;
        onClose?: () => void;
        signal?: AbortSignal;
    }) {
        const {
            agentId,
            conversationId,
            messages,
            messageId,
            retryAttempt = 0,
            onOpen,
            onMessage,
            onError,
            onClose,
            signal
        } = params;

        // CRITICAL: Generate stable ID BEFORE stream
        // If messageId provided (retry), use it. Otherwise generate new UUID.
        const assistantMessageId = messageId || uuidv4();

        console.debug('[ChatService] Initiating authenticated stream...', {
            agentId,
            conversationId,
            messageId: assistantMessageId,
            retryAttempt
        });

        const apiUrl = import.meta.env.VITE_API_URL || '';
        await createAuthenticatedStream(`${apiUrl}/api/v1/chat/completions`, {
            agent_id: agentId,
            messages: messages,
            conversation_id: conversationId,
            message_id: assistantMessageId, // ALWAYS send stable ID
            stream: true
        }, {
            signal,
            headers: {
                'X-Retry-Attempt': String(retryAttempt) // Track retry attempts
            },
            onopen: async () => {
                if (onOpen) onOpen();
            },
            onChunk: (data) => {
                if (data === '[DONE]') return;

                try {
                    const json = JSON.parse(data);

                    // Log error if present in stream
                    if (json.error) {
                        console.error('[ChatService] Stream returned error chunk:', json.error);
                        onError(new Error(json.error));
                        return;
                    }

                    const deltaContent = json.choices?.[0]?.delta?.content || '';
                    if (deltaContent) {
                        onMessage(deltaContent, assistantMessageId);
                    }
                } catch (e) {
                    console.error('[ChatService] Failed to parse SSE chunk:', e);
                }
            },
            onerror: (err) => {
                onError(err instanceof Error ? err : new Error(String(err)));
            },
            onclose: () => {
                if (onClose) onClose();
            }
        });
    }
}
